import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { auth, db } from './firebase';
import { 
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { 
  doc, 
  getDoc,
  onSnapshot,
  collection,
  setDoc,
  serverTimestamp,
  runTransaction,
  query,
  where,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { 
  getSession, 
  createSession, 
  clearSession,
  refreshSession,
  getSessionUser,
  upgradeSessionToFullUser
} from './users/sessionManagement';
import { 
  createOrUpdateUserDocument, 
  upgradeGuestUserToFull, 
  isGuestUser,
  getUserProfileByEmail,
  createUserDocument,
  sendWelcomeEmailIfNeeded,
  reclaimUploadedAvatar
} from './users/userManagement';
import { handleRSVPAfterSignIn, addRSVP } from './events/eventServices';
import { useNotification } from './components/NotificationContext';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { checkGroupMembership, joinGroup } from './groups/groupServices';
import { updateGroupMembershipsByEmail } from './utils/userUpdateUtils';
import { verifyAuthCode, invalidateAllCodesForEmail } from './auth/codeAuthService';
import { signInWithGoogle } from './auth/googleAuthService';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const updateUser = useCallback((updatedUser) => {
    if (updatedUser && (updatedUser.uid || updatedUser.id)) {
      const finalUser = {
        ...updatedUser,
        uid: updatedUser.uid || updatedUser.id
      };
      setUser(prevUser => {
        if (JSON.stringify(prevUser) !== JSON.stringify(finalUser)) {
          console.log('Updating user:', finalUser);
          return finalUser;
        }
        return prevUser;
      });
    } else {
      console.error('Attempted to update user with invalid data:', updatedUser);
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (user && user.email) {
      const refreshedUser = await getUserProfileByEmail(user.email);
      if (refreshedUser && JSON.stringify(refreshedUser) !== JSON.stringify(user)) {
        updateUser(refreshedUser);
      }
    }
  }, [user, updateUser]);

  useEffect(() => {
    const initializeAuth = async () => {
      await setPersistence(auth, browserLocalPersistence);
      
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('Auth state changed. User:', firebaseUser ? firebaseUser.email : 'null', 'Stack trace:', new Error().stack);
        if (firebaseUser) {
          console.log('Calling createOrUpdateUserDocument for:', firebaseUser.email);
          let userDoc = await createOrUpdateUserDocument(firebaseUser);
          console.log('createOrUpdateUserDocument completed for:', firebaseUser.email);
          // Self-heal legacy photoURL: if an uploaded avatar exists in Storage
          // but photoURL points elsewhere (e.g. a Google photo saved before we
          // separated googlePhotoURL), restore the uploaded avatar.
          userDoc = await reclaimUploadedAvatar(userDoc);
          updateUser(userDoc);
          createSession(firebaseUser.email, false);
          
          if (userDoc.isNewUser) {
            await sendWelcomeEmailIfNeeded(userDoc);
          }
          
          await updateGroupMembershipsByEmail(firebaseUser.email, firebaseUser.uid);
        } else {
          const session = getSession();
          if (session) {
            let sessionUser = await getSessionUser();
            if (sessionUser) {
              // Self-heal legacy photoURL for email-code sessions too.
              sessionUser = await reclaimUploadedAvatar(sessionUser);
              updateUser(sessionUser);
            } else {
              setUser(null);
              clearSession();
            }
          } else {
            setUser(null);
          }
        }
        setLoading(false);
      });

      return () => unsubscribe();
    };

    initializeAuth();
  }, [updateUser]);

  const mockSignIn = async (email) => {
    console.log(`Mock sign-in for ${email}`);
    let userDoc = await getUserProfileByEmail(email);
    if (!userDoc) {
      return { success: false, message: 'User not found. Please create a test account first.' };
    }
    const mockUser = {
      ...userDoc,
      uid: userDoc.uid,
      email,
      isGuest: false,
      isTestAccount: false,
    };
    updateUser(mockUser);
    createSession(email, false);

    // Update group memberships
    console.log('Updating group memberships after mock sign-in');
    await updateGroupMembershipsByEmail(email, mockUser.uid);

    return { success: true, message: 'Mock sign-in successful!', user: mockUser };
  };

  const login = async (email, useMockSignIn = false, isRSVP = false, eventId = null, code = null, options = {}) => {
    const skipRSVPInAuth = options.skipRSVP === true;
    console.log(`Login attempt for email: ${email}, useMockSignIn: ${useMockSignIn}, isRSVP: ${isRSVP}, hasCode: ${!!code}, skipRSVPInAuth: ${skipRSVPInAuth}`);
    
    try {
      if (useMockSignIn && process.env.NODE_ENV === 'development') {
        console.log('Using mock sign-in');
        return await mockSignIn(email);
      }

      // If code is provided, verify it
      if (code) {
        const verifyResult = await verifyAuthCode(email, code);
        
        if (!verifyResult.success || !verifyResult.valid) {
          return { 
            success: false, 
            message: verifyResult.error || 'Invalid or expired code. Please request a new one.' 
          };
        }

        // Code is valid – use auth code data as source of truth for RSVP (eventId, rsvpStatus)
        const codeData = verifyResult.data || {};
        const rsvpEventId = codeData.eventId || eventId;
        const rsvpStatus = codeData.rsvpStatus || 'yes';
        const shouldRSVP = (codeData.isRSVP && rsvpEventId) || (isRSVP && eventId);

        const userDoc = await getUserProfileByEmail(email);
        
        if (userDoc) {
          // Existing user - just sign them in
          // All users are now full users (no guest concept)
          // Re-fetch to ensure we have latest data (e.g., welcomeEmailSent flag)
          const refreshedUser = await getUserProfileByEmail(email);
          const finalUser = refreshedUser || userDoc;
          
          updateUser(finalUser);
          createSession(email, false);
          upgradeSessionToFullUser();
          refreshSession();
          
          // Send welcome email if this is their first time signing in
          // Only send if they haven't already received one (e.g., from RSVP flow)
          await sendWelcomeEmailIfNeeded(finalUser);
          
          // Invalidate all codes for this email
          await invalidateAllCodesForEmail(email);
          
          // Handle RSVP if needed – skip when caller will do it (e.g. event page modal adds RSVP in onSuccess and shows confetti)
          if (shouldRSVP && !skipRSVPInAuth) {
            await handleRSVPAfterSignIn(email, rsvpEventId, showNotification, rsvpStatus, codeData.guestCount || 0);
          }
          
          // Update group memberships
          await updateGroupMembershipsByEmail(email, finalUser.uid || finalUser.id);
          
          // Return celebrateRsvp whenever this was an RSVP flow so event page can add RSVP + send emails (when skipRSVP) or we already did it here
          const celebrateRsvp = shouldRSVP ? { eventId: rsvpEventId, rsvpStatus, guestCount: codeData.guestCount || 0 } : null;
          return { success: true, message: 'Signed in successfully!', user: finalUser, celebrateRsvp };
        } else {
          // New user - create full user account
          // Normalize email and check if user already exists
          const normalizedEmail = email.toLowerCase().trim();
          
          // Simple check: does a user with this email already exist?
          const existingUser = await getUserProfileByEmail(normalizedEmail);
          
          if (existingUser) {
            // User already exists, use existing user
            updateUser(existingUser);
            createSession(normalizedEmail, false);
            upgradeSessionToFullUser();
            refreshSession();
            
            // Invalidate all codes for this email
            await invalidateAllCodesForEmail(normalizedEmail);
            
            if (shouldRSVP && !skipRSVPInAuth) {
              await handleRSVPAfterSignIn(normalizedEmail, rsvpEventId, showNotification, rsvpStatus, codeData.guestCount || 0);
            }
            
            // Update group memberships
            await updateGroupMembershipsByEmail(normalizedEmail, existingUser.uid || existingUser.id);
            
            const celebrateRsvp = shouldRSVP ? { eventId: rsvpEventId, rsvpStatus, guestCount: codeData.guestCount || 0 } : null;
            return { 
              success: true, 
              message: 'Signed in successfully!', 
              user: existingUser, 
              isNewUser: false,
              celebrateRsvp
            };
          }
          
          // User doesn't exist, create new user (name from auth code doc when they signed up)
          const pendingName = (verifyResult.data?.pendingName || '').trim();
          const newUserRef = doc(collection(db, 'users'));
          const newUser = {
            uid: newUserRef.id,
            email: normalizedEmail,
            name: pendingName,
            photoURL: '',
            isGuest: false,
            isTestAccount: false,
            createdAt: serverTimestamp(),
            welcomeEmailSent: false,
          };
          
          await setDoc(newUserRef, newUser);
          
          // Use the user we just wrote (with name from sign-up) so profile and app state match the DB
          const verifyUser = await getUserProfileByEmail(normalizedEmail);
          let finalUser = verifyUser && verifyUser.uid === newUserRef.id
            ? verifyUser
            : newUser;
          let isNewUserFlag = true;
          
          if (verifyUser && verifyUser.uid !== newUserRef.id) {
            // Race condition: Another request created a user with this email first
            console.log(`Race condition detected: User ${verifyUser.uid} exists, deleting duplicate ${newUserRef.id}`);
            finalUser = verifyUser;
            isNewUserFlag = false;
            await deleteDoc(newUserRef).catch(error => {
              console.error('Error deleting duplicate user:', error);
            });
          }
          
          updateUser(finalUser);
          createSession(normalizedEmail, false);
          upgradeSessionToFullUser();
          refreshSession();
          
          // Send welcome email only for truly new users
          if (isNewUserFlag) {
            await sendWelcomeEmailIfNeeded(finalUser);
          }
          
          // Invalidate all codes for this email
          await invalidateAllCodesForEmail(normalizedEmail);
          
          if (shouldRSVP && !skipRSVPInAuth) {
            await handleRSVPAfterSignIn(normalizedEmail, rsvpEventId, showNotification, rsvpStatus, codeData.guestCount || 0);
          }
          
          // Update group memberships
          await updateGroupMembershipsByEmail(normalizedEmail, finalUser.uid || finalUser.id);
          
          const celebrateRsvp = shouldRSVP ? { eventId: rsvpEventId, rsvpStatus, guestCount: codeData.guestCount || 0 } : null;
          return { 
            success: true, 
            message: isNewUserFlag ? 'Account created successfully!' : 'Signed in successfully!', 
            user: finalUser, 
            isNewUser: isNewUserFlag,
            celebrateRsvp
          };
        }
      } else {
        // No code provided - this is just requesting a code (handled in Login component)
        // Return success so Login component can proceed with code generation
        return { success: true, message: 'Code request initiated' };
      }
    } catch (error) {
      console.error('Error during login process:', error);
      return { success: false, message: 'Error during login process: ' + error.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const firebaseUser = await signInWithGoogle();
      // Explicitly sync Firestore doc — onAuthStateChanged may not settle in the same tick,
      // so we need the userDoc immediately for callers like the RSVP modal's onSuccess.
      let userDoc = await createOrUpdateUserDocument(firebaseUser);
      // Self-heal legacy photoURL so an uploaded avatar isn't shadowed by a
      // stale Google photo saved before we separated googlePhotoURL.
      userDoc = await reclaimUploadedAvatar(userDoc);
      createSession(firebaseUser.email, false);
      await updateGroupMembershipsByEmail(firebaseUser.email, firebaseUser.uid);
      if (userDoc.isNewUser) {
        await sendWelcomeEmailIfNeeded(userDoc);
      }
      updateUser(userDoc);
      return { success: true, user: userDoc };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, message: null }; // silent cancel — user dismissed popup
      }
      if (error.code === 'auth/popup-blocked') {
        return { success: false, message: 'Please allow popups for this site to sign in with Google.' };
      }
      console.error('Google sign-in error:', error);
      return { success: false, message: error.message || 'Google sign-in failed. Please try again.' };
    }
  };

  const handleRSVP = async (email, eventId) => {
    console.log(`Handling RSVP for email: ${email}, eventId: ${eventId}`);
    try {
      const userDoc = await getUserProfileByEmail(email);
      
      if (userDoc) {
        // Existing user: return success so modal can handle code sending
        // The LoginCodeModal will send the code when it opens (no duplicate emails)
        return { success: true, message: 'Please sign in to complete your RSVP' };
      } else {
        // New user: create user profile and session, then add RSVP
        // RSVP flow - send welcome email immediately
        const newUser = await createUserDocument(email, '', true);
        createSession(email, false); // Full user, not guest
        updateUser(newUser);
        await addRSVP(eventId, { userId: newUser.uid || newUser.id, email }, email);
        showNotification('Account created and RSVP confirmed!', 'success');
        return { success: true, message: 'Account created and RSVP confirmed!', user: newUser };
      }
    } catch (error) {
      console.error('Error during RSVP process:', error);
      showNotification('Error during RSVP process: ' + error.message, 'error');
      return { success: false, message: 'Error during RSVP process: ' + error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      clearSession();
      setUser(null);
      showNotification('Logged out successfully', 'success');
    } catch (error) {
      console.error('Error during logout:', error);
      showNotification('Error during logout: ' + error.message, 'error');
    }
  };

  const checkGroupAccess = async (groupName) => {
    if (!user) return false;
    return await checkGroupMembership(user.uid, groupName);
  };

  const joinGroupWithCode = async (groupName, email, groupCode) => {
    const result = await joinGroup(groupName, email, groupCode);
    if (result.success) {
      await refreshUser();
    }
    return result;
  };

  const value = {
    user,
    setUser: updateUser,  // Make sure this line is present
    login,
    loginWithGoogle,
    logout,
    handleRSVP,
    isAuthenticated: !!user,
    isGuest: user?.isGuest || false,
    isTestUser: user?.isTestAccount || false,
    refreshUser,
    checkGroupAccess,
    joinGroupWithCode,
    authLoading: loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
